import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  FormControlLabel,
  Switch,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import PaginationBar from '../../common/PaginationBar';

/**
 * ThemeTable Component
 *
 * Displays themes in a table format with:
 * - Theme information (name, code, status, colors)
 * - Action buttons (edit, delete)
 * - Pagination controls
 */
const ThemeTable = ({
  themes,
  page,
  rowsPerPage,
  onEditTheme,
  onDeleteTheme,
  onToggleTheme,
  onChangePage,
  onChangeRowsPerPage,
}) => {
  const totalCount = themes?.length || 0;
  const totalPages = Math.ceil(totalCount / rowsPerPage) || 1;

  // PaginationBar handlers (1-based pagination)
  const handlePrevPage = () => {
    if (page > 0) {
      onChangePage(null, page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      onChangePage(null, page + 1);
    }
  };

  const handlePageClick = newPage => {
    // Convert 1-based to 0-based
    onChangePage(null, newPage - 1);
  };

  const handlePageSizeChange = newSize => {
    onChangeRowsPerPage({ target: { value: newSize } });
  };

  return (
    <>
      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Theme Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Primary Color</TableCell>
              <TableCell>Font Family</TableCell>
              <TableCell>Custom CSS</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {themes?.map(theme => (
              <TableRow key={theme.theme_id}>
                <TableCell>
                  <Typography variant="subtitle2">
                    {theme.theme_name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {theme.theme_description || theme.description || ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={theme.theme_code}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={theme.is_active}
                        onChange={e =>
                          onToggleTheme(theme.theme_id, e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label={theme.is_active ? 'Active' : 'Inactive'}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        backgroundColor: theme.primary_color,
                        borderRadius: 1,
                        mr: 1,
                        border: '1px solid #ccc',
                      }}
                    />
                    <Typography variant="body2">
                      {theme.primary_color}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{theme.font_family}</Typography>
                </TableCell>
                <TableCell>
                  {theme.custom_css ? (
                    <Chip label="Custom CSS" size="small" color="secondary" />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      None
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title="Edit Theme">
                    <IconButton onClick={() => onEditTheme(theme)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  {false && (
                    <Tooltip title="Delete Theme">
                      <IconButton onClick={() => onDeleteTheme(theme.theme_id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {totalPages > 1 && (
        <Box sx={{ mt: 2 }}>
          <PaginationBar
            currentPage={page + 1} // Convert 0-based to 1-based
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={rowsPerPage}
            onPrev={handlePrevPage}
            onNext={handleNextPage}
            onPageClick={handlePageClick}
            onPageSizeChange={handlePageSizeChange}
          />
        </Box>
      )}
    </>
  );
};

export default ThemeTable;
